/**
 * SessionConfigTab — Admin SADAdmin · Système · Session
 *
 * Permet de configurer les durées de session inactivité globalement :
 *   - Durée d'inactivité avant warning (en minutes, bornes 5 → 480)
 *   - Délai du modal warning avant logout auto (en secondes, bornes 30 → 600)
 *
 * La modification s'applique aux nouvelles connexions ET aux sessions existantes
 * à leur prochain refresh (chaque login refetch /api/system/session-config).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Save, RotateCcw, Info } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const DEFAULTS = { idle_minutes: 30, warning_seconds: 120 };

const SessionConfigTab = () => {
  const [bounds, setBounds] = useState({ min_idle: 300, max_idle: 28800, min_warn: 30, max_warn: 600 });
  const [idleMin, setIdleMin] = useState(DEFAULTS.idle_minutes);
  const [warnSec, setWarnSec] = useState(DEFAULTS.warning_seconds);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/system/session-config`, { headers: auth() });
      setIdleMin(Math.round((r.data.idle_seconds || 1800) / 60));
      setWarnSec(r.data.warning_seconds || 120);
      setBounds({
        min_idle: r.data.min_idle_seconds || 300,
        max_idle: r.data.max_idle_seconds || 28800,
        min_warn: r.data.min_warning_seconds || 30,
        max_warn: r.data.max_warning_seconds || 600,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de charger la configuration');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const idleSec = idleMin * 60;
    if (idleSec <= warnSec) {
      toast.error("Le délai d'inactivité doit être strictement supérieur au délai de warning.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/system/session-config`, {
        idle_seconds: idleSec,
        warning_seconds: warnSec,
      }, { headers: auth() });
      toast.success('Configuration enregistrée — appliquée à la prochaine connexion.');
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const text = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join(' · ')
        : 'Erreur lors de l\'enregistrement';
      toast.error(text);
    } finally { setSaving(false); }
  };

  const reset = () => {
    setIdleMin(DEFAULTS.idle_minutes);
    setWarnSec(DEFAULTS.warning_seconds);
  };

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="session-config-tab">
      <div className="flex items-center gap-3">
        <Clock className="w-8 h-8 text-amber-600" />
        <div>
          <h2 className="text-xl font-bold text-zinc-900">Configuration de session</h2>
          <p className="text-sm text-zinc-500">Délais de déconnexion automatique pour inactivité (paramètres globaux).</p>
        </div>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardContent className="p-6 space-y-5">
          {loading ? (
            <p className="text-sm text-zinc-400 text-center py-6">Chargement…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="idle-min" className="text-sm font-medium text-zinc-700">
                    Durée d&apos;inactivité avant avertissement
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="idle-min"
                      type="number"
                      min={Math.round(bounds.min_idle / 60)}
                      max={Math.round(bounds.max_idle / 60)}
                      value={idleMin}
                      onChange={(e) => setIdleMin(Math.max(0, parseInt(e.target.value || '0', 10)))}
                      className="w-24"
                      data-testid="session-idle-input"
                    />
                    <span className="text-sm text-zinc-500">minutes</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">
                    Entre <strong>{Math.round(bounds.min_idle / 60)}</strong> et <strong>{Math.round(bounds.max_idle / 60)}</strong> minutes.
                  </p>
                </div>

                <div>
                  <Label htmlFor="warn-sec" className="text-sm font-medium text-zinc-700">
                    Délai d&apos;avertissement (popup) avant logout auto
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="warn-sec"
                      type="number"
                      min={bounds.min_warn}
                      max={bounds.max_warn}
                      value={warnSec}
                      onChange={(e) => setWarnSec(Math.max(0, parseInt(e.target.value || '0', 10)))}
                      className="w-24"
                      data-testid="session-warn-input"
                    />
                    <span className="text-sm text-zinc-500">secondes</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">
                    Entre <strong>{bounds.min_warn}</strong> et <strong>{bounds.max_warn}</strong> secondes.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-semibold">Comportement appliqué côté utilisateur :</p>
                  <ul className="text-xs list-disc list-inside space-y-0.5">
                    <li>Après <strong>{idleMin - Math.round(warnSec / 60)} min</strong> sans activité, un modal s&apos;ouvre avec un compte à rebours de <strong>{warnSec}s</strong>.</li>
                    <li>L&apos;utilisateur peut cliquer <strong>« Rester connecté »</strong> ou <strong>« Se déconnecter »</strong>.</li>
                    <li>Sans action après <strong>{idleMin} min</strong> au total, logout auto.</li>
                    <li>Toute interaction (souris, clavier, scroll, tactile) réinitialise le timer.</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={reset}
                  className="text-zinc-600"
                  data-testid="session-reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1" /> Valeurs recommandées (30 min · 2 min)
                </Button>
                <Button
                  onClick={save}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="session-save-btn"
                >
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionConfigTab;
